import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Plus, Trash, Plugs as Webhook, CheckCircle, Warning, Clock, Copy, Code,
  ArrowsClockwise, Link, ShieldCheck, PaperPlaneTilt, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebhooksManager({ open, onClose }) {
  const [webhooks, setWebhooks] = useState([]);
  const [eventDocs, setEventDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [selectedExample, setSelectedExample] = useState("shipment.created");
  const [revealedSecrets, setRevealedSecrets] = useState({});

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [wh, docs] = await Promise.all([
        axios.get(`${API}/webhooks/`),
        axios.get(`${API}/webhooks/event-types/examples`),
      ]);
      setWebhooks(wh.data || []);
      setEventDocs(docs.data);
    } catch (e) { toast.error("Failed to load webhooks"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) fetchAll(); }, [open, fetchAll]);

  const handleDelete = async (id) => {
    if (!confirm("Delete this webhook?")) return;
    try {
      await axios.delete(`${API}/webhooks/${id}`);
      toast.success("Webhook deleted");
      fetchAll();
    } catch (e) { toast.error("Delete failed"); }
  };

  const handleTest = async (id) => {
    try {
      await axios.post(`${API}/webhooks/${id}/test`);
      toast.success("Test event sent — check your endpoint");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  const copy = (text, label = "Copied") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="webhooks-manager-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Webhook size={18} weight="duotone" className="text-teal-400" />
              Webhooks &amp; Integrations
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDocs(true)}
                className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
                data-testid="view-integration-docs-btn">
                <Code size={12} className="mr-1" /> Integration Docs
              </Button>
              <Button onClick={() => setShowCreate(true)}
                className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8"
                data-testid="new-webhook-btn">
                <Plus size={12} className="mr-1" /> New Webhook
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">
            Receive real-time events for shipments and alerts in your TMS, ERP, or any HTTP endpoint. All deliveries are HMAC-SHA256 signed.
          </p>
        </DialogHeader>

        {/* List */}
        {loading ? (
          <div className="text-xs font-mono text-zinc-500 animate-pulse text-center py-12">Loading...</div>
        ) : webhooks.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800 p-10 text-center" data-testid="webhooks-empty">
            <Webhook size={32} weight="duotone" className="text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-400 mb-1">No webhooks configured</p>
            <p className="text-xs text-zinc-600 mb-3">Create one to push real-time events to your TMS, ERP, or Slack.</p>
            <Button onClick={() => setShowCreate(true)} className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8">
              <Plus size={12} className="mr-1" /> Create webhook
            </Button>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="webhooks-list">
            {webhooks.map(wh => (
              <Card key={wh.id} className="bg-zinc-900 border-zinc-800 p-4">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-sm ${wh.active ? "bg-teal-500/15" : "bg-zinc-700/30"}`}>
                    {wh.active ? <CheckCircle size={14} className="text-teal-400" weight="duotone" /> : <Warning size={14} className="text-zinc-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-zinc-100 truncate">{wh.name || "Webhook"}</h3>
                      <Badge variant="outline" className="border-zinc-700 text-[9px] uppercase font-mono">
                        {wh.active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                    <p className="text-[11px] font-mono text-zinc-400 mt-1 truncate flex items-center gap-1">
                      <Link size={10} /> {wh.url}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-2" data-testid={`webhook-events-${wh.id}`}>
                      {(wh.events || []).map((e) => (
                        <span key={e} className="text-[9px] font-mono px-1.5 py-0.5 bg-zinc-800 text-zinc-300 rounded-sm border border-zinc-700">
                          {e}
                        </span>
                      ))}
                    </div>
                    {/* Stats */}
                    <div className="flex items-center gap-4 mt-2 text-[10px] font-mono text-zinc-500">
                      <span>{wh.delivery_count || 0} deliveries</span>
                      {wh.last_delivery_at && <span><Clock size={9} className="inline mr-0.5" />{wh.last_delivery_at.substring(0, 19)}</span>}
                    </div>
                    {/* Secret reveal */}
                    {wh.secret && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[9px] uppercase tracking-wider text-zinc-600">Secret:</span>
                        <code className="text-[10px] font-mono text-zinc-400 bg-zinc-950 px-2 py-0.5 rounded-sm border border-zinc-800">
                          {revealedSecrets[wh.id] ? wh.secret : "•".repeat(Math.min(wh.secret.length, 30))}
                        </code>
                        <button onClick={() => setRevealedSecrets(s => ({ ...s, [wh.id]: !s[wh.id] }))}
                          className="text-zinc-500 hover:text-teal-400" title="Toggle secret">
                          {revealedSecrets[wh.id] ? <EyeSlash size={11} /> : <Eye size={11} />}
                        </button>
                        <button onClick={() => copy(wh.secret, "Secret copied")} className="text-zinc-500 hover:text-teal-400" title="Copy secret">
                          <Copy size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button onClick={() => handleTest(wh.id)} variant="outline" size="sm"
                      className="border-zinc-700 hover:bg-zinc-800 text-xs h-8"
                      data-testid={`test-webhook-${wh.id}`}>
                      <PaperPlaneTilt size={11} className="mr-1" /> Test
                    </Button>
                    <button onClick={() => handleDelete(wh.id)}
                      className="p-2 text-zinc-500 hover:text-red-400"
                      data-testid={`delete-webhook-${wh.id}`}>
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Create dialog */}
        <CreateWebhookDialog open={showCreate} onClose={() => setShowCreate(false)}
          eventTypes={eventDocs?.event_types || []} onCreated={fetchAll} />

        {/* Integration docs dialog */}
        <Dialog open={showDocs} onOpenChange={setShowDocs}>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl max-h-[90vh] overflow-y-auto"
            data-testid="integration-docs-dialog">
            <DialogHeader>
              <DialogTitle className="text-zinc-100 flex items-center gap-2">
                <Code size={16} weight="duotone" className="text-teal-400" />
                Webhook Integration Guide
              </DialogTitle>
              <p className="text-[11px] text-zinc-500 mt-1">Share this with your TMS / ERP integrator.</p>
            </DialogHeader>

            {eventDocs && (
              <div className="space-y-4">
                {/* Delivery contract */}
                <Card className="bg-zinc-900 border-zinc-800 p-4">
                  <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-1.5">
                    <ShieldCheck size={11} className="text-teal-400" /> Delivery Contract
                  </h4>
                  <div className="space-y-2 text-[11px] font-mono">
                    <ContractRow label="Method" value={eventDocs.delivery.method} />
                    <ContractRow label="Content-Type" value={eventDocs.delivery.content_type} />
                    <ContractRow label="Signature" value={`${eventDocs.delivery.signature_algorithm} (${eventDocs.delivery.signature_format})`} />
                    <ContractRow label="Retry policy" value={eventDocs.delivery.retry_policy} />
                    <ContractRow label="Expected response" value={eventDocs.delivery.expected_response} />
                  </div>
                  <div className="mt-3">
                    <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Headers</p>
                    <div className="space-y-1">
                      {Object.entries(eventDocs.delivery.headers).map(([k, v]) => (
                        <div key={k} className="flex gap-2 text-[10px] font-mono">
                          <span className="text-teal-400">{k}:</span>
                          <span className="text-zinc-400">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>

                {/* Verification snippet */}
                <Card className="bg-zinc-900 border-zinc-800 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Verify Signature (Python)</h4>
                    <button onClick={() => copy(eventDocs.delivery.verification_example_python, "Snippet copied")}
                      className="text-zinc-500 hover:text-teal-400 text-[10px] flex items-center gap-1"
                      data-testid="copy-verify-snippet-btn">
                      <Copy size={11} /> Copy
                    </button>
                  </div>
                  <pre className="text-[10px] font-mono text-zinc-300 bg-zinc-950 p-3 rounded-sm border border-zinc-800 overflow-x-auto">
{eventDocs.delivery.verification_example_python}
                  </pre>
                </Card>

                {/* Event examples */}
                <Card className="bg-zinc-900 border-zinc-800 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Event Payload Examples</h4>
                    <button onClick={() => copy(JSON.stringify(eventDocs.examples[selectedExample], null, 2), "Payload copied")}
                      className="text-zinc-500 hover:text-teal-400 text-[10px] flex items-center gap-1"
                      data-testid="copy-payload-btn">
                      <Copy size={11} /> Copy JSON
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3" data-testid="event-type-tabs">
                    {eventDocs.event_types.map(et => (
                      <button key={et} onClick={() => setSelectedExample(et)}
                        data-testid={`event-tab-${et}`}
                        className={`text-[10px] font-mono px-2 py-1 rounded-sm border transition-colors ${
                          selectedExample === et
                            ? "bg-teal-500/20 text-teal-400 border-teal-700"
                            : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-700"
                        }`}>
                        {et}
                      </button>
                    ))}
                  </div>
                  <ScrollArea className="h-[300px]">
                    <pre className="text-[10px] font-mono text-zinc-300 bg-zinc-950 p-3 rounded-sm border border-zinc-800 overflow-x-auto"
                      data-testid="payload-preview">
{JSON.stringify(eventDocs.examples[selectedExample], null, 2)}
                    </pre>
                  </ScrollArea>
                </Card>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

function ContractRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-zinc-500 w-32 shrink-0">{label}:</span>
      <span className="text-zinc-300">{value}</span>
    </div>
  );
}

function CreateWebhookDialog({ open, onClose, eventTypes, onCreated }) {
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(""); setUrl(""); setEvents([]);
    }
  }, [open]);

  const toggleEvent = (e) => {
    setEvents(s => s.includes(e) ? s.filter(x => x !== e) : [...s, e]);
  };

  const handleSubmit = async () => {
    if (!url.trim()) { toast.error("URL is required"); return; }
    if (events.length === 0) { toast.error("Pick at least one event"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/webhooks/`, { name: name.trim() || "Webhook", url: url.trim(), events });
      toast.success("Webhook created. Save the secret — it's shown once.");
      onCreated?.();
      onClose();
      // Optional: copy secret on success
      if (res.data?.secret) navigator.clipboard.writeText(res.data.secret);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="create-webhook-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">New Webhook</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)}
              placeholder="TMS Integration" className="bg-zinc-900 border-zinc-800 text-sm mt-1"
              data-testid="webhook-form-name" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">URL *</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-tms.example.com/webhooks/holotrak"
              className="bg-zinc-900 border-zinc-800 text-sm mt-1 font-mono"
              data-testid="webhook-form-url" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-400">Events ({events.length})</Label>
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-2 mt-1 max-h-44 overflow-y-auto space-y-1">
              {eventTypes.map(et => (
                <label key={et} className="flex items-center gap-2 text-xs px-1 py-0.5 hover:bg-zinc-800 rounded-sm cursor-pointer"
                  data-testid={`webhook-event-${et}`}>
                  <input type="checkbox" checked={events.includes(et)} onChange={() => toggleEvent(et)}
                    className="accent-teal-500" />
                  <span className="font-mono text-zinc-300">{et}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="border-zinc-700 text-xs h-8">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting}
              className="bg-teal-500 hover:bg-teal-600 text-zinc-950 text-xs h-8"
              data-testid="webhook-submit-btn">
              {submitting ? "Creating..." : "Create Webhook"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
